import { useState } from 'react';
import {
  Alert, Button, Card, KpiCard, Spinner, useToast,
} from '@kss/ui';
import { migrationService, type GrantAdminResult, type SeedMenusResult } from '@kss/api';
import {
  AlertTriangle, CheckCircle2, Database, Layers, Play, RefreshCw, ShieldCheck, Users,
} from 'lucide-react';

/**
 * Operator-facing UI for the §11/§12 backend endpoints:
 *   POST /api/admin/migration/seed-menus
 *   POST /api/admin/migration/grant-admin-everything
 *
 * Restricted to SuperAdmin (the backend enforces it; the frontend just
 * surfaces the action). Two side-by-side actions:
 *
 *   1. **Re-seed menus** — picks up newly-added menu codes after a
 *      schema bump, so dashboards stop showing the static fallback.
 *   2. **Grant admin everything** — fans out across every join table
 *      so the bootstrap admin user sees the full sidebar.
 *
 * Both are idempotent and rendered with a per-stage progress card.
 */

interface IncludeFlags {
  roles: boolean;
  permissions: boolean;
  menus: boolean;
  branches: boolean;
  warehouses: boolean;
}

const DEFAULT_INCLUDE: IncludeFlags = {
  roles: true,
  permissions: true,
  menus: true,
  branches: true,
  warehouses: true,
};

export default function GrantAdminPage() {
  const toast = useToast();
  const [username, setUsername] = useState('admin');
  const [include, setInclude] = useState<IncludeFlags>(DEFAULT_INCLUDE);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GrantAdminResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Re-seed menus (§11) lives on the same page since both are operator
  // recovery actions and run against the same tenant scope.
  const [seedForce, setSeedForce] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedResult, setSeedResult] = useState<SeedMenusResult | null>(null);
  const [seedErr, setSeedErr] = useState<string | null>(null);

  async function reSeed() {
    setSeedBusy(true);
    setSeedErr(null);
    setSeedResult(null);
    try {
      // tenantId is required by the backend but defaults to the JWT's
      // tenant on the controller side when omitted. Sending empty is the
      // documented signal.
      const data = await migrationService.seedMenus({ tenantId: '', force: seedForce });
      setSeedResult(data);
      const total =
        data.menus.inserted + data.permissions.inserted + data.roles.inserted + data.rolePermissions.inserted;
      toast.success('Menus re-seeded', `${total.toLocaleString()} rows inserted in ${data.durationMs} ms`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Request failed';
      setSeedErr(msg);
      toast.error('Could not re-seed', msg);
    } finally {
      setSeedBusy(false);
    }
  }

  async function run() {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const data = await migrationService.grantAdminEverything({
        username: username.trim() || 'admin',
        include,
      });
      setResult(data);
      toast.success(
        'Admin re-granted',
        data?.totalRows != null
          ? `${data.totalRows.toLocaleString()} rows across ${data.stages?.length ?? 0} stages`
          : 'Done',
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Request failed';
      setErr(msg);
      toast.error('Could not grant', msg);
    } finally {
      setBusy(false);
    }
  }

  const totalRows = result?.totalRows ?? result?.stages?.reduce((s, st) => s + st.inserted + st.updated, 0) ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Grant admin everything</h2>
          <p className="text-xs text-content-secondary">
            Re-syncs the bootstrap admin user against every role, permission, menu, branch and warehouse for this tenant.
          </p>
        </div>
      </div>

      <Alert variant="warning">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="text-xs">
            These are recovery / re-seed actions. Use them after schema changes that added new menus or permissions, or
            if an admin&rsquo;s grants were accidentally trimmed. Both endpoints are idempotent — safe to run twice.
            Requires <code>SuperAdmin</code> role; the backend enforces this.
          </div>
        </div>
      </Alert>

      {/* --------- §11  Re-seed menus / permissions / roles --------- */}
      <Card>
        <div className="mb-2 flex items-center gap-2">
          <Database className="h-4 w-4 text-info" />
          <h3 className="text-sm font-semibold">Re-seed menus, permissions &amp; roles</h3>
        </div>
        <p className="text-xs text-content-secondary">
          Walks <code>sidebarNav.json</code> and ensures every menu, permission, role and
          role-permission row exists for this tenant. Run after deploying new modules.
        </p>
        <label className="mt-2 inline-flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={seedForce}
            onChange={(e) => setSeedForce(e.target.checked)}
          />
          <span>Force re-write existing rows (otherwise upsert keyed on code)</span>
        </label>
        <div className="mt-3 flex items-center gap-2">
          <Button variant="outline" onClick={reSeed} disabled={seedBusy}>
            {seedBusy ? <Spinner /> : <RefreshCw className="h-3.5 w-3.5" />}
            Re-seed
          </Button>
          {seedBusy && <span className="text-xs text-content-muted">Working through the catalogue…</span>}
        </div>
        {seedErr && <Alert variant="danger" className="mt-3">{seedErr}</Alert>}
        {seedResult && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-content-muted">
                  <th className="py-2 pr-2">Table</th>
                  <th className="py-2 pr-2 text-right">Inserted</th>
                  <th className="py-2 pr-2 text-right">Updated</th>
                  <th className="py-2 pr-2 text-right">Skipped</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(['menus', 'permissions', 'roles', 'rolePermissions'] as const).map((k) => {
                  const r = seedResult[k];
                  return (
                    <tr key={k}>
                      <td className="py-2 pr-2 font-medium capitalize">{k.replace(/([A-Z])/g, ' $1').trim()}</td>
                      <td className="py-2 pr-2 text-right tabular-nums text-success">{r.inserted.toLocaleString()}</td>
                      <td className="py-2 pr-2 text-right tabular-nums text-info">{r.updated.toLocaleString()}</td>
                      <td className="py-2 pr-2 text-right tabular-nums text-content-muted">{r.skipped.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="mt-2 text-[11px] text-content-muted">Completed in {seedResult.durationMs} ms.</p>
          </div>
        )}
      </Card>

      {/* --------- §12  Grant the bootstrap admin everything --------- */}
      <Card>
        <div className="mb-2 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-success" />
          <h3 className="text-sm font-semibold">Grant admin everything</h3>
        </div>
        <p className="mb-2 text-xs text-content-secondary">
          Resolves the union of every menu / permission / role / branch / warehouse and writes
          the join-table rows that connect the admin user to all of them.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs">
            <span className="mb-1 block font-medium text-content-secondary">Admin username</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm outline-none focus:border-primary"
              placeholder="admin"
            />
          </label>
          <div>
            <span className="mb-1 block text-xs font-medium text-content-secondary">Include</span>
            <div className="flex flex-wrap gap-3 text-xs">
              {(Object.keys(DEFAULT_INCLUDE) as (keyof IncludeFlags)[]).map((k) => (
                <label key={k} className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={include[k]}
                    onChange={(e) => setInclude((cur) => ({ ...cur, [k]: e.target.checked }))}
                  />
                  <span className="capitalize">{k}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button onClick={run} disabled={busy}>
            {busy ? <Spinner /> : <Play className="h-3.5 w-3.5" />}
            Run grant
          </Button>
          {busy && <span className="text-xs text-content-muted">This usually takes 1–3 seconds…</span>}
        </div>
      </Card>

      {err && <Alert variant="danger">{err}</Alert>}

      {result && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title="Stages run"
              value={(result.stages?.length ?? 0).toLocaleString()}
              subtitle="Join tables touched"
              icon={<Layers className="h-5 w-5 text-info" />}
            />
            <KpiCard
              title="Rows changed"
              value={totalRows.toLocaleString()}
              subtitle="Insert + update"
              icon={<CheckCircle2 className="h-5 w-5 text-success" />}
            />
            <KpiCard
              title="User"
              value={result.username ?? '—'}
              subtitle={result.userId ?? ''}
              icon={<Users className="h-5 w-5 text-primary" />}
            />
            <KpiCard
              title="Duration"
              value={result.durationMs != null ? `${result.durationMs} ms` : '—'}
              subtitle={result.tenantId ?? 'Tenant'}
              icon={<ShieldCheck className="h-5 w-5 text-success" />}
            />
          </div>

          {result.note && <Alert variant="info">{result.note}</Alert>}

          <Card>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-muted">
              Per-stage breakdown
            </h3>
            {!result.stages?.length ? (
              <div className="p-4 text-xs text-content-muted">No stages reported.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-content-muted">
                      <th className="py-2 pr-2">Stage</th>
                      <th className="py-2 pr-2 text-right">Inserted</th>
                      <th className="py-2 pr-2 text-right">Updated</th>
                      <th className="py-2 pr-2 text-right">Skipped</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {result.stages.map((s) => (
                      <tr key={s.name}>
                        <td className="py-2 pr-2 font-medium">{s.name}</td>
                        <td className="py-2 pr-2 text-right tabular-nums text-success">{s.inserted.toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right tabular-nums text-info">{s.updated.toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right tabular-nums text-content-muted">{s.skipped.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
